import { ExtraParamsKey } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { define, object, optional, string, validate } from 'superstruct';

import { isNewOttFeatureEnabled } from '@/constants/env';
import { useCustomRegistrationConfig } from '@/pages/CustomRegister/hooks/use-custom-registration-config';
import ErrorPage from '@/pages/ErrorPage';
import { searchKeys } from '@/shared/utils/search-parameters';

import { canRefreshOneTimeToken, getOneTimeTokenClientId } from './can-refresh-one-time-token.js';
import { isNewOttGateFlow } from './is-new-ott-gate-flow.js';
import { renewMagicLink } from './refresh-one-time-token.js';

// Runtime guard for i18n key strings while preserving TFuncKey typing at compile-time
const tFunctionKey = define<TFuncKey>('TFuncKey', (value) => typeof value === 'string');
const stateGuard = object({
  title: optional(tFunctionKey),
  message: optional(tFunctionKey),
  errorCode: optional(string()),
  errorMessage: optional(string()),
  email: optional(string()),
});

/**
 * Error page:
 * - New OTT gate (FF on, `source` not a classic flow value) → "Token is expired."
 * - Renew button → allowlist only (separate from flow selection).
 * - otherwise → classic invalid-link page.
 */
const Error = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const [, parsed] = validate(state, stateGuard);
  const config = useCustomRegistrationConfig();
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string>();

  const clientId = getOneTimeTokenClientId(
    searchParams,
    sessionStorage.getItem(searchKeys.appId) ?? undefined
  );
  const email = parsed?.email ?? searchParams.get(ExtraParamsKey.LoginHint) ?? undefined;
  const isFeatureEnabled = config?.newOttFeatureEnabled ?? isNewOttFeatureEnabled;

  const isNewOttFlow = isNewOttGateFlow(searchParams, isFeatureEnabled);
  const canRefresh =
    isNewOttFlow && canRefreshOneTimeToken(clientId, config?.magicLinkRenewClientIds);

  const handleRefresh = async () => {
    if (!email || !clientId) {
      setRefreshError('Magic link is missing required parameters.');
      return;
    }

    setIsRefreshing(true);
    setRefreshError(undefined);

    try {
      await renewMagicLink(email, clientId);
      setIsEmailSent(true);
    } catch (error: unknown) {
      setRefreshError(
        error instanceof globalThis.Error ? error.message : 'Could not refresh the magic link.'
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isNewOttFlow && isEmailSent) {
    return (
      <ErrorPage isNavbarHidden title="error.invalid_link" rawTitle="Please check your email" />
    );
  }

  if (isNewOttFlow) {
    return (
      <ErrorPage
        isNavbarHidden
        title="error.invalid_link"
        rawTitle="Token is expired."
        rawMessage={
          refreshError ?? parsed?.errorMessage ?? searchParams.get('errorMessage') ?? undefined
        }
        primaryAction={
          canRefresh
            ? {
                label: 'Refresh token',
                isLoading: isRefreshing,
                onClick: () => {
                  void handleRefresh();
                },
              }
            : undefined
        }
      />
    );
  }

  return (
    <ErrorPage
      isNavbarHidden
      title={parsed?.title ?? 'error.invalid_link'}
      message={parsed?.message ?? 'error.invalid_link_description'}
      rawMessage={parsed?.errorMessage ?? searchParams.get('errorMessage') ?? undefined}
    />
  );
};

export default Error;
